import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import Card from "../../../../../components/layouts/card/card";
import ReturnMethodStore from "../../../stores/return-method-store";
import FormFieldLineLayout from "../../../../../components/layouts/form-field-line-layout/form-field-line-layout";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { Input } from "../../../../../components/inputs/input/input";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { CARRIER_NOTIFICATION, WEIGHT_UNIT } from "../../../return-method-types";
import ConnectionSelector from "./connection-selector/connection-selector";
import TrackingTypeFields from "./tracking-type-fields/tracking-type-fields";
import PackageDimensionsFields from "./package-dimensions-fields/package-dimensions-fields";
import LabelTypeFields from "./label-type-fields/label-type-fields";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";
import CarrierSelectionOptionsFields from "./carrier-selection-options-fields/carrier-selection-options-fields";
import ConnectionSettingsFields from "./connection-settings-fields/connection-settings-fields";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";

interface Props {
    returnMethod?: ReturnMethodStore;
    validationGroup: IValidationGroup;
}

const ShippingLabelSettingsCard: React.FC<Props> = observer(({returnMethod, validationGroup}: Props) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-return-method-editor';
    if (!returnMethod){
        return null;
    }

    const shippingLabelStore = returnMethod.shippingLabelSetting;

    useEffect(() => {
        shippingLabelStore?.load();
    }, [shippingLabelStore, shippingLabelStore?.shippingConnectionId]);

    const weightUnitList = shippingLabelStore?.populateWeightUnitList() as IOption[];
    const selectedWeightUnit = () => {
        const weightUnit = shippingLabelStore?.weightUnit;
        if (!weightUnit) {
            return undefined;
        }
        return weightUnitList?.find( unit => unit.value === weightUnit);
    }

    const carrierNotificationsList = shippingLabelStore?.setCarrierNotificationsList(getTranslation) as IOption[];
    const selectedCarrierNotifications = () => {
        const carrierNotifications = shippingLabelStore?.carrierNotifications;
        if (!carrierNotifications) {
            return undefined;
        }
        return carrierNotificationsList?.find( notification => notification.value === carrierNotifications);
    }


    return(
        <Card
            toolbar={{
                title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Shipping Label Settings</FontBody1>,
            }}
            isExpanded={false}
            isLoading={shippingLabelStore?.isLoadign()}
        >
            <FontBody2>Integrate to your shipping service and customize the settings used to generate return labels.</FontBody2>
            <FormFieldLineLayout>
                <ConnectionSelector returnMethodStore={returnMethod} validationGroup={validationGroup}/>
                <TrackingTypeFields shippingLabelStore={shippingLabelStore} />

                {   shippingLabelStore?.isSupportCarrierSelectionOptions &&
                    <CarrierSelectionOptionsFields shippingLabelStore={shippingLabelStore} validationGroup={validationGroup}/>
                }

                {
                    !shippingLabelStore?.isCoverageConnection &&
                    !shippingLabelStore?.isSupportCarrierSelectionOptions &&
                    <ConnectionSettingsFields shippingLabelStore={shippingLabelStore} validationGroup={validationGroup}/>
                }
	              <LabelTypeFields returnMethod={returnMethod} validationGroup={validationGroup} />

                {!!shippingLabelStore?.isDefaultWeightRequired() &&
                    <>
                        <FormFieldLine
                            label={getTranslation("DEFAULT_ITEM_WEIGHT", scope)}
                            note={getTranslation("DEFAULT_ITEM_WEIGHT_NOTE", scope)}
                            required
                        >
                            <Input
                                type="number" temp
                                value={shippingLabelStore?.weight}
                                onChange={shippingLabelStore?.setWeight}
                                validationCallback={shippingLabelStore?.validateWeight}
                                required
                                validationGroup={validationGroup}
                            />
                        </FormFieldLine>
                        <FormFieldLine
                            label={getTranslation("SHIPMENT_WEIGHT", scope)}
                            note={getTranslation("SHIPMENT_WEIGHT_NOTE", scope)}    
                        >
                            <Input
                                type="number" temp
                                value={shippingLabelStore?.packageWeight}
                                onChange={shippingLabelStore?.setPackageWeight}
                                validationGroup={validationGroup}
                            />
                        </FormFieldLine>
                        <FormFieldLine
                            label={getTranslation("WEIGHT_UNIT", scope)}
                            note={getTranslation("WEIGHT_UNIT_NOTE", scope)}
                            required
                        >
                            <SelectDropdown
                                value={selectedWeightUnit()}
                                options={weightUnitList}
                                onChange={(v) => shippingLabelStore?.setWeightUnit(v?.value as WEIGHT_UNIT)}
                                required
                                validationGroup={validationGroup}
                            />
                        </FormFieldLine>
                </>}

                { !shippingLabelStore?.hideCustomDimensions() &&
                    <PackageDimensionsFields shippingLabelStore={shippingLabelStore} validationGroup={validationGroup}/>
                }
                <FormFieldLine
                    label={getTranslation("CONFIGURE_CARRIER_NOTIFICATIONS", scope)}
                    note={getTranslation("CONFIGURE_CARRIER_NOTIFICATIONS_NOTE", scope)}
                >
                    <SelectDropdown
                        value={selectedCarrierNotifications()}
                        options={carrierNotificationsList}
                        onChange={(v) => shippingLabelStore?.setCarrierNotifications(v?.value as CARRIER_NOTIFICATION)}
                    />
                </FormFieldLine>
            </FormFieldLineLayout>

        </Card>
    )
})

export default ShippingLabelSettingsCard;
